import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import GlobalErrorBoundary from "@/components/GlobalErrorBoundary";
import Navbar from "@/components/Navbar";
import ProtectedRoute from "@/components/ProtectedRoute";
import PageSkeleton from "@/components/PageSkeleton";
import { Suspense, lazy, useEffect, useState } from "react";

// Heavy widget (framer-motion + react-markdown chunk + supabase queries).
// Lazy-load it AND defer mounting until the browser is idle so it doesn't
// add to main-thread parse/eval cost on initial paint.
const FloatingSupportWidget = lazy(() => import("@/components/FloatingSupportWidget"));

const DeferredSupportWidget = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const trigger = () => setShow(true);
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(trigger, { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(trigger, 2000);
    return () => window.clearTimeout(id);
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <FloatingSupportWidget />
    </Suspense>
  );
};

// Lazy-load all pages
const Landing = lazy(() => import("@/pages/Landing"));
const BrowseRequests = lazy(() => import("@/pages/BrowseRequests"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const BuyerDashboard = lazy(() => import("@/pages/BuyerDashboard"));
const BuyerProposals = lazy(() => import("@/pages/buyer/BuyerProposals"));
const BuyerTransactions = lazy(() => import("@/pages/buyer/BuyerTransactions"));
const BuyerSettings = lazy(() => import("@/pages/buyer/BuyerSettings"));
const SellerDashboard = lazy(() => import("@/pages/SellerDashboard"));
const SellerSubmissions = lazy(() => import("@/pages/seller/SellerSubmissions"));
const SellerCredits = lazy(() => import("@/pages/seller/SellerCredits"));
const SellerEarnings = lazy(() => import("@/pages/seller/SellerEarnings"));
const SellerSettings = lazy(() => import("@/pages/seller/SellerSettings"));
const SellerPortfolio = lazy(() => import("@/pages/seller/SellerPortfolio"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminRequests = lazy(() => import("@/pages/admin/AdminRequests"));
const AdminTransactions = lazy(() => import("@/pages/admin/AdminTransactions"));
const AdminPayouts = lazy(() => import("@/pages/admin/AdminPayouts"));
const AdminKyc = lazy(() => import("@/pages/admin/AdminKyc"));
const AdminSupport = lazy(() => import("@/pages/admin/AdminSupport"));
const AdminDealChats = lazy(() => import("@/pages/admin/AdminDealChats"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const RequestDetail = lazy(() => import("@/pages/RequestDetail"));
const MyRequestDetail = lazy(() => import("@/pages/MyRequestDetail"));
const KycVerification = lazy(() => import("@/pages/KycVerification"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const Terms = lazy(() => import("@/pages/Terms"));
const EscrowPolicy = lazy(() => import("@/pages/EscrowPolicy"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const Disputes = lazy(() => import("@/pages/Disputes"));
const Faq = lazy(() => import("@/pages/Faq"));
const LegalInformation = lazy(() => import("@/pages/LegalInformation"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const About = lazy(() => import("@/pages/About"));
const FundCustody = lazy(() => import("@/pages/FundCustody"));
const BuyerProtection = lazy(() => import("@/pages/BuyerProtection"));
const Compliance = lazy(() => import("@/pages/Compliance"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry once with exponential backoff. Three retries on every failed
      // request causes a noticeable hang (network round-trips × 3) when the
      // user briefly loses connection or hits an RLS denial.
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
  },
});

const App = () => (
  <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Navbar />
            <Suspense fallback={<PageSkeleton />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/browse" element={<BrowseRequests />} />
                <Route path="/requests/:id" element={<RequestDetail />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/escrow-policy" element={<EscrowPolicy />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/disputes" element={<Disputes />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/legal" element={<LegalInformation />} />
                <Route path="/about" element={<About />} />
                <Route path="/fund-custody" element={<FundCustody />} />
                <Route path="/buyer-protection" element={<BuyerProtection />} />
                <Route path="/compliance" element={<Compliance />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/kyc" element={<ProtectedRoute><KycVerification /></ProtectedRoute>} />

                {/* Buyer routes, viewable after signup; actions are gated by KycGate */}
                <Route path="/dashboard/buyer" element={<ProtectedRoute allowedRoles={["buyer"]} requireKyc={false}><BuyerDashboard /></ProtectedRoute>} />
                <Route path="/dashboard/buyer/proposals" element={<ProtectedRoute allowedRoles={["buyer"]} requireKyc={false}><BuyerProposals /></ProtectedRoute>} />
                <Route path="/dashboard/buyer/transactions" element={<ProtectedRoute allowedRoles={["buyer"]} requireKyc={false}><BuyerTransactions /></ProtectedRoute>} />
                <Route path="/dashboard/buyer/settings" element={<ProtectedRoute allowedRoles={["buyer"]} requireKyc={false}><BuyerSettings /></ProtectedRoute>} />
                
                <Route path="/my-requests/:id" element={<ProtectedRoute allowedRoles={["buyer"]} requireKyc={false}><MyRequestDetail /></ProtectedRoute>} />

                {/* Seller routes, viewable after signup; actions are gated by KycGate */}
                <Route path="/dashboard/seller" element={<ProtectedRoute allowedRoles={["seller"]} requireKyc={false}><SellerDashboard /></ProtectedRoute>} />
                <Route path="/dashboard/seller/portfolio" element={<ProtectedRoute allowedRoles={["seller"]} requireKyc={false}><SellerPortfolio /></ProtectedRoute>} />
                <Route path="/dashboard/seller/submissions" element={<ProtectedRoute allowedRoles={["seller"]} requireKyc={false}><SellerSubmissions /></ProtectedRoute>} />
                <Route path="/dashboard/seller/earnings" element={<ProtectedRoute allowedRoles={["seller"]} requireKyc={false}><SellerEarnings /></ProtectedRoute>} />
                <Route path="/dashboard/seller/credits" element={<ProtectedRoute allowedRoles={["seller"]} requireKyc={false}><SellerCredits /></ProtectedRoute>} />
                <Route path="/dashboard/seller/settings" element={<ProtectedRoute allowedRoles={["seller"]} requireKyc={false}><SellerSettings /></ProtectedRoute>} />
                

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/dashboard/users" element={<ProtectedRoute allowedRoles={["admin"]}><AdminUsers /></ProtectedRoute>} />
                <Route path="/admin/dashboard/requests" element={<ProtectedRoute allowedRoles={["admin"]}><AdminRequests /></ProtectedRoute>} />
                <Route path="/admin/dashboard/transactions" element={<ProtectedRoute allowedRoles={["admin"]}><AdminTransactions /></ProtectedRoute>} />
                <Route path="/admin/dashboard/payouts" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPayouts /></ProtectedRoute>} />
                <Route path="/admin/dashboard/kyc" element={<ProtectedRoute allowedRoles={["admin"]}><AdminKyc /></ProtectedRoute>} />
                <Route path="/admin/dashboard/support" element={<ProtectedRoute allowedRoles={["admin"]}><AdminSupport /></ProtectedRoute>} />
                <Route path="/admin/dashboard/chats" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDealChats /></ProtectedRoute>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <DeferredSupportWidget />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </GlobalErrorBoundary>
);

export default App;
